
import {
  Building2,
  Monitor,
  Briefcase,
  Megaphone,
  HardHat,
  ShoppingCart,
  Rocket,
  Sparkles,
  Check,
  type LucideIcon,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { IndustryTemplate } from "@/lib/onboarding/industry-templates";

const iconMap: Record<string, LucideIcon> = {
  Building2,
  Monitor,
  Briefcase,
  Megaphone,
  HardHat,
  ShoppingCart,
};

interface IndustrySelectorProps {
  industries: IndustryTemplate[];
  onSelect: (industry: IndustryTemplate | null) => void;
  selectedId?: string | null;
  onSelectedIdChange?: (id: string) => void;
  onStartWithAi?: () => void;
}

export function IndustrySelector({
  industries,
  onSelect,
  selectedId,
  onSelectedIdChange,
  onStartWithAi,
}: IndustrySelectorProps) {
  return (
    <div className="space-y-4">
      {onStartWithAi ? (
        <>
          <div className="flex flex-col gap-3 rounded-lg border bg-muted/30 p-3.5 sm:flex-row sm:items-center">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <Sparkles className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">Set up with AI</p>
                <p className="mt-0.5 text-xs text-muted-foreground [overflow-wrap:anywhere]">
                  Describe what you sell. The assistant proposes deal types and
                  creates them for you.
                </p>
              </div>
            </div>
            <Button
              type="button"
              size="sm"
              className="w-full shrink-0 sm:w-auto"
              onClick={onStartWithAi}
            >
              Describe your business
            </Button>
          </div>

          <div className="flex items-center gap-2.5">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground">
              or pick an industry
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>
        </>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {industries.map((industry) => {
          const Icon = iconMap[industry.icon] || Briefcase;
          const isSelected = industry.id === selectedId;
          return (
            <button
              key={industry.id}
              type="button"
              onClick={() => onSelectedIdChange?.(industry.id)}
              aria-pressed={isSelected}
              className={cn(
                "flex min-w-0 flex-col items-start gap-2 rounded-lg border-2 p-4 text-left transition-colors",
                isSelected
                  ? "border-foreground bg-muted/40"
                  : "border-muted bg-popover hover:bg-accent hover:border-primary"
              )}
            >
              <div className="flex w-full min-w-0 items-center justify-between gap-2">
                <Icon className="h-6 w-6 shrink-0 text-primary" />
                <div className="flex shrink-0 items-center gap-2">
                  <Badge variant="secondary" className="shrink-0">
                    {industry.dealTypes.length} types
                  </Badge>
                  {isSelected && (
                    <span className="flex size-[18px] shrink-0 items-center justify-center rounded-full bg-foreground text-background">
                      <Check className="size-3" />
                    </span>
                  )}
                </div>
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-sm [overflow-wrap:anywhere]">
                  {industry.name}
                </p>
                <p className="text-xs text-muted-foreground [overflow-wrap:anywhere]">
                  {industry.description}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => onSelect(null)}
        className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-muted p-3 text-sm text-muted-foreground transition-colors hover:bg-accent hover:border-primary hover:text-foreground"
      >
        <Rocket className="h-4 w-4" />
        Start from scratch
      </button>
    </div>
  );
}
